import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { resolveSelfServiceApiUrl } from '../../shared/self-service-api.utils';
import type { DesignNode } from '../design-builder.types';
import type { DesignScreen } from '../utils/design-publish.utils';
import { DesignRuntimeService } from '../data/design-runtime.service';
import { normalizeIconAction } from '../utils/design-icon-actions.utils';

/** One instance per screen preview, so thumbnails and other screens cannot open its panel. */
@Injectable()
export class DesignIconActionsService {
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient, { optional: true });
  readonly runtime = inject(DesignRuntimeService, { optional: true });
  readonly panel = signal<'filter' | 'search' | 'info' | null>(null);
  readonly infoTitle = signal('Information');
  readonly infoText = signal('');
  readonly infoLoading = signal(false);
  readonly infoError = signal('');
  private infoRequest = 0;
  readonly message = signal('');

  async execute(node: DesignNode, screen: DesignScreen): Promise<void> {
    this.message.set('');
    try {
      switch (normalizeIconAction(node.icon?.action)) {
        case 'none': return;
        case 'info': await this.showInfo(node); return;
        case 'filter':
        case 'search':
          this.panel.set(node.icon!.action as 'filter' | 'search');
          return;
        case 'reset':
        case 'refresh':
          if (!this.runtime) throw new Error('Dashboard data is not available in this view.');
          this.runtime.refreshDashboard(node.icon?.action === 'reset');
          return;
        case 'download': this.download(screen); return;
        case 'upload': this.pickFile(node, screen); return;
        case 'print': this.print(screen); return;
        case 'share': await this.share(screen); return;
      }
    } catch (error) {
      // Dismissing the native share dialog is an intentional cancellation.
      if ((error as { name?: string })?.name !== 'AbortError') {
        this.message.set(error instanceof Error ? error.message : 'The dashboard action could not be completed.');
      }
    }
  }

  private async showInfo(node: DesignNode): Promise<void> {
    const request = ++this.infoRequest;
    const info = node.icon?.info;
    this.panel.set('info');
    this.infoTitle.set(info?.title?.trim() || 'Information');
    this.infoText.set('');
    this.infoError.set('');
    this.infoLoading.set(false);
    if (info?.source !== 'api') {
      this.infoText.set(info?.text?.trim() || 'No information configured.');
      return;
    }
    if (!info.url.trim() || !this.http) {
      this.infoError.set('Configure an information API URL in the icon settings.');
      return;
    }
    this.infoLoading.set(true);
    try {
      const url = resolveSelfServiceApiUrl(info.url.trim(), info.url.trim());
      const response = await firstValueFrom(this.http.get(url, { responseType: 'text' }).pipe(timeout(30000)));
      if (request !== this.infoRequest) return;
      let value: unknown = response;
      try { value = JSON.parse(response); } catch { /* Plain-text responses are supported. */ }
      if (info.responsePath.trim()) {
        for (const part of info.responsePath.trim().split('.')) {
          value = value !== null && typeof value === 'object' ? (value as Record<string, unknown>)[part] : undefined;
        }
        if (value === undefined) throw new Error('The configured response text path was not found.');
      }
      this.infoText.set(value == null || value === '' ? 'No information available.' : typeof value === 'string' ? value : JSON.stringify(value, null, 2));
    } catch (error) {
      if (request === this.infoRequest) this.infoError.set(error instanceof Error && error.message.includes('response text path')
        ? error.message : 'Could not load information. Close and reopen to try again.');
    } finally {
      if (request === this.infoRequest) this.infoLoading.set(false);
    }
  }

  private download(screen: DesignScreen): void {
    if (!this.runtime) throw new Error('Dashboard data is not available in this view.');
    const ids = new Set(flattenScreen(screen).map((node) => node.id));
    const texts = flattenScreen(screen).filter((node) => node.dataBinding).map((node) => ({
      id: node.id, name: node.name, value: this.runtime!.textValues()[node.dataBinding!.componentId ?? ''] ?? null,
    }));
    const data = {
      screen: screen.name,
      exportedAt: new Date().toISOString(),
      filters: this.runtime.activeFilters(),
      widgets: this.runtime.tiles().filter((tile) => ids.has(tile.id)).map((tile) => ({
        id: tile.id, name: tile.title, layoutType: tile.layoutType, data: this.runtime!.exportDataFor(tile.id),
      })),
      texts,
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const anchor = this.document.createElement('a');
    anchor.href = url;
    anchor.download = `${screen.name.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'dashboard'}-data.json`;
    this.document.body.appendChild(anchor);
    try { anchor.click(); } finally { anchor.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
    this.message.set('Dashboard data downloaded as JSON.');
  }

  private pickFile(node: DesignNode, screen: DesignScreen): void {
    const target = flattenScreen(screen).find((item) => item.id === node.icon?.targetNodeId && item.widget?.layoutType === 'FILE_UPLOAD');
    const control = target && this.runtime?.tileFor(target.id)?.fileUploadControl;
    if (!target || !control) throw new Error('Choose a generated file upload control on this screen in the icon Action settings.');
    const picker = this.document.createElement('input');
    picker.type = 'file';
    picker.hidden = true;
    picker.accept = control.accept ?? '';
    // A single upload at a time follows the runtime file-upload contract.
    picker.addEventListener('change', () => {
      const file = picker.files?.[0];
      if (file) this.uploadFiles(node, screen, [file]);
      picker.remove();
    }, { once: true });
    picker.addEventListener('cancel', () => picker.remove(), { once: true });
    this.document.body.appendChild(picker);
    picker.click();
  }

  /** Click selection and file drops share target validation and the upload API. */
  uploadFiles(node: DesignNode, screen: DesignScreen, files: File[]): void {
    this.message.set('');
    if (node.icon?.action !== 'upload') return;
    const target = flattenScreen(screen).find((item) => item.id === node.icon?.targetNodeId && item.widget?.layoutType === 'FILE_UPLOAD');
    const control = target && this.runtime?.tileFor(target.id)?.fileUploadControl;
    if (!target || !control) { this.message.set('Choose a generated file upload control on this screen in the icon Action settings.'); return; }
    if (files.length !== 1) { this.message.set('Upload one file at a time.'); return; }
    const file = files[0];
    const allowed = (control.accept ?? '').split(',').map((type) => type.trim().toLowerCase()).filter(Boolean);
    if (allowed.length && !allowed.some((type) => type.startsWith('.') ? file.name.toLowerCase().endsWith(type)
      : type.endsWith('/*') ? file.type.toLowerCase().startsWith(type.slice(0, -1)) : file.type.toLowerCase() === type)) {
      this.message.set(`This file type is not accepted. Allowed: ${control.accept}`);
      return;
    }
    this.runtime!.onFilePicked(target.id, file);
  }

  private async share(screen: DesignScreen): Promise<void> {
    const win = this.document.defaultView;
    if (!win) throw new Error('Sharing is unavailable in this browser.');
    const url = new URL(win.location.href);
    // Builder links include editing state and do not identify a published page.
    if (!/\/dvm-published\/[^/]+/.test(url.pathname)) {
      throw new Error('Open the published dashboard to share its link.');
    }
    url.search = '';
    url.hash = '';
    if (win.navigator.share) {
      await win.navigator.share({ title: screen.name, url: url.href });
      this.message.set('Dashboard link shared.');
    } else if (win.navigator.clipboard?.writeText) {
      await win.navigator.clipboard.writeText(url.href);
      this.message.set('Dashboard link copied.');
    } else {
      throw new Error('Link sharing is unavailable in this browser.');
    }
  }

  private print(screen: DesignScreen): void {
    const win = this.document.defaultView;
    if (!win) throw new Error('Printing is unavailable in this browser.');
    // The preview marks its own host; print CSS hides app chrome and other screens.
    const hosts = Array.from(this.document.querySelectorAll<HTMLElement>('[data-design-screen]'));
    const host = hosts.find((element) => element.dataset['designScreen'] === screen.id);
    if (!host) throw new Error('The dashboard screen could not be found for printing.');
    host.classList.add('dsg-print-target');
    const style = this.document.createElement('style');
    style.textContent = '@media print { body * { visibility: hidden !important; } .dsg-print-target, .dsg-print-target * { visibility: visible !important; } .dsg-print-target { position: absolute !important; inset: 0 !important; width: 100% !important; height: auto !important; overflow: visible !important; } .dsg-print-target .dsg-icon-action, .dsg-print-target .dsg-action-feedback, .dsg-print-target .dsg-action-panel, .dsg-print-target .dsg-zoom-pill { display: none !important; } }';
    this.document.head.appendChild(style);
    try { win.print(); } finally { host.classList.remove('dsg-print-target'); style.remove(); }
  }
}

export function flattenScreen(screen: DesignScreen): DesignNode[] {
  const result: DesignNode[] = [];
  const walk = (nodes: DesignNode[]): void => nodes.forEach((node) => {
    if (!node.visible) return;
    result.push(node);
    if (node.children) walk(node.children);
  });
  walk(screen.nodes);
  return result;
}
